import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Gear } from "@phosphor-icons/react";
import { useProgressStore } from "../../app/stores/progressStore";
import { getUnits, COURSES, type CourseId } from "../engine/lessonRegistry";
import { coursePath } from "../../app/routes";
import { Button } from "../../app/components/ui/Button";
import { SettingsDialog } from "../../ui/SettingsDialog";
import type { DialogHandle } from "../../app/components/ui/Dialog";
import styles from "./HomeView.module.css";

const LOGO_SRC = "/images/logo.png";

export function HomeView() {
  const navigate = useNavigate();
  const courses = useProgressStore((s) => s.courses);
  const settingsRef = useRef<DialogHandle>(null);

  const openCourse = (course: CourseId): void => {
    navigate(coursePath(course));
  };

  return (
    <main className={styles.home}>
      <section className={styles.hero} aria-labelledby="home-title">
        <div className={styles.logoBadge}>
          <img
            src={LOGO_SRC}
            alt="Stanford Karel"
            className={styles.logo}
            width={232}
            height={40}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
        <Button
          variant="secondary"
          className={styles.settings}
          icon={<Gear size={16} />}
          aria-label="Settings"
          title="Settings"
          onClick={() => settingsRef.current?.open()}
        >
          Settings
        </Button>
        <p className={styles.tagline}>
          Learn to program by guiding Karel through a world of puzzles.
        </p>
      </section>

      <section className={styles.courseSection} aria-label="Courses">
        <div className={styles.courseCards}>
          {COURSES.map((course) => {
            const registry = getUnits(course.id);
            const progress = courses[course.id];
            const totalLessons = registry.reduce(
              (sum, unit) => sum + unit.lessons.length,
              0,
            );
            const completedLessons = progress.units.reduce(
              (sum, unit) =>
                sum + unit.statuses.filter((s) => s === "finished").length,
              0,
            );
            const ratio =
              totalLessons > 0 ? completedLessons / totalLessons : 0;

            return (
              <button
                key={course.id}
                type="button"
                className={styles.courseCard}
                onClick={() => openCourse(course.id)}
              >
                <span className={styles.courseName}>{course.name}</span>
                <span className={styles.courseDescription}>
                  {course.description}
                </span>
                <span className={styles.courseProgressTrack}>
                  <span
                    className={styles.courseProgressFill}
                    style={{ width: `${Math.round(ratio * 100)}%` }}
                  />
                </span>
                <span className={styles.courseFooter}>
                  <span className={styles.courseProgressText}>
                    {completedLessons} of {totalLessons} lessons
                  </span>
                  <ArrowRight size={18} weight="bold" aria-hidden="true" />
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <SettingsDialog ref={settingsRef} />
    </main>
  );
}
